import get from 'lodash/get';
import isFunction from 'lodash/isFunction';
import {Observable} from 'rxjs/Observable';
import {createResourceSortOptionsSelector, createResourceSortsSelector} from 'viewOptions/selectors';
import {filterOptionsFactory} from 'viewOptions/factories';
import {createResourceSelector} from 'resources/selectors';
import * as ViewOptionsActions from 'viewOptions/actions';
import {SortDirection, isUnsorted} from 'utils/DBUtils';
import * as PrizeServiceApi from 'api/prize-service';
import * as ResourceActions from 'resources/actions';
import {getPrizeEntryFilters} from './selectors';
import * as ResourceApi from 'api/resources';
import * as WinnersActions from './actions';
import {isFeatured} from './utils';

const getSortOptions = createResourceSortOptionsSelector(ResourceApi.Types.PrizeEntry);
const getSorts = createResourceSortsSelector(ResourceApi.Types.PrizeEntry);

const requestWinnersEpic = (action$, store) => action$.ofType(
  WinnersActions.Types.REQUEST_WINNERS,
  ResourceActions.Types.UPDATE_PAGE_NUMBER_FOR_RESOURCE,
  ViewOptionsActions.Types.TOGGLE_SORT_FOR_RESOURCE,
  ViewOptionsActions.Types.SET_RAW_FILTERS_FOR_RESOURCE
)
  .filter(action => {
    if (action.type === WinnersActions.Types.REQUEST_WINNERS) {
      return true;
    } else {
      return action.resourceType === ResourceApi.Types.PrizeEntry && action.params.winnersOnly === true;
    }
  })
  .mergeMap(action => {
    const state = store.getState();
    const {currentPrizeType, defaultSort} = state.listWinners;
    const prizeType = currentPrizeType || action.prizeType;

    let sortOptions;

    // if the collection is not currently sorted, toggle default field's sort direction.
    if (isUnsorted(getSorts(state))) {
      sortOptions = getSortOptions(state);
      sortOptions.sorts[defaultSort] = SortDirection.Descending;
    } else {
      // otherwise defer to whatever sorts are currently in place (cf. `resources` epic).
      sortOptions = null;
    }

    const filterOptions = filterOptionsFactory({
      filters: [
        ...getPrizeEntryFilters(state),
        {field: 'type', value: prizeType},
        {field: 'winner', value: true}
      ]
    });

    return Observable.of(
      WinnersActions.requestedWinnersAction(prizeType),
      ResourceActions.getResourcePageAction(
        ResourceApi.Types.PrizeEntry,
        20,
        action.page || 1,
        sortOptions,
        filterOptions
      )
    );
  });

const receivedWinnersEpic = (action$, store) => action$.ofType(ResourceActions.Types.RECEIVED_RESOURCE_PAGE)
  .filter(action => {
    const {fetchingWinners} = store.getState().listWinners;
    return fetchingWinners && action.resourceType === ResourceApi.Types.PrizeEntry;
  })
  .mapTo(WinnersActions.receivedWinnersAction());



const saveDateOrderedEpic = action$ => action$.ofType(WinnersActions.Types.SUBMIT_SAVE_DATE_ORDERED)
  .map(action => {
    const resourceId = {
      type: ResourceApi.Types.PrizeEntry,
      id: action.winnerId
    };

    return ResourceActions.saveResourceFieldAction(
      resourceId,
      resourceId,
      [],
      ['dateOrdered'],
      'dateOrdered',
      action.dateOrdered,
      [resourceId]
    );
  });

const saveWinnerStoryEpic = (action$, store) => action$.ofType(WinnersActions.Types.SAVE_WINNER_STORY)
  .map(action => {
    const {currentWinnerId} = store.getState().listWinners;
    const resourceId = {
      type: ResourceApi.Types.PrizeEntry,
      id: currentWinnerId
    };

    return ResourceActions.saveResourceFieldAction(
      resourceId,
      resourceId,
      [],
      ['winnerStory'],
      'winnerStory',
      action.winnerStoryWithEdits,
      [resourceId]
    );
  });

const saveWinnerNotesEpic = (action$, store) => action$.ofType(WinnersActions.Types.SAVE_NOTES)
    .map(action => {
        const {editingWinnerNotes} = store.getState().listWinners;
        const resourceId = {
            type: ResourceApi.Types.PrizeEntry,
            id: editingWinnerNotes
        };

        return ResourceActions.saveResourceFieldAction(
            resourceId,
            resourceId,
            [],
            ['fulfillmentNotes'],
            'fulfillmentNotes',
            action.notes,
            [resourceId]
        );
    });

const savedWinnerFieldEpic = action$ => action$.ofType(ResourceActions.Types.RESOURCE_FIELD_SAVED)
  .filter(action => action.rootResourceId.type === ResourceApi.Types.PrizeEntry)
  .mergeMap(action => {
    // action creators that accept either an id or nothing
    const actionsByField = {
      dateOrdered: WinnersActions.saveDateOrderedSuccessAction,
      winnerStory: WinnersActions.closeEditWinnerStoryModalAction,
      fulfillmentNotes: WinnersActions.saveWinnerNotesSuccessAction,
    };

    const createAction = actionsByField[action.field];
    return isFunction(createAction) ? Observable.of(createAction(action.id)) : Observable.empty();
  });

const errorSavingWinnerFieldEpic = action$ => action$.ofType(ResourceActions.Types.SAVE_RESOURCE_FIELD_ERROR)
  .filter(action => action.rootResourceId.type === ResourceApi.Types.PrizeEntry)
  .mergeMap(action => {
    const actionsByField = {
      dateOrdered: WinnersActions.saveDateOrderedErrorAction,
      winnerStory: WinnersActions.editWinnerStoryErrorAction,
      fulfillmentNotes: WinnersActions.saveWinnerNotesErrorAction,
    };

    const createAction = actionsByField[action.field];
    return isFunction(createAction) ? Observable.of(createAction(action.id)) : Observable.empty();
  });

const toggleFeaturedWinnerEpic = (action$, store) => action$.ofType(WinnersActions.Types.SUBMIT_TOGGLE_FEATURED_WINNER)
  .mergeMap(action => {
    const state = store.getState();
    const {currentWinnerId} = state.listWinners;
    const currentWinner = createResourceSelector(currentWinnerId)(state.resources);

    // different service API calls depending on which way the featured flag is being toggled.
    const serviceAction = isFeatured(currentWinner) ? 'makeWinnerUnfeatured' : 'makeWinnerFeatured';
    const toggleFeatured = PrizeServiceApi[serviceAction];

    return toggleFeatured(currentWinnerId, currentWinner.lang)
      .mapTo(WinnersActions.toggleFeaturedWinnerSuccessAction(currentWinnerId))
      .catch(error => {
        if (error.status === 403) {
          console.error('User account has insufficient permissions.');
        }

        return Observable.of(
          WinnersActions.toggleFeaturedWinnerErrorAction(currentWinnerId),
          error.response.errorCode === 'MAX_FEATURED_WINNER_STORIES'
            ? WinnersActions.openFeaturedErrorModalAction(currentWinner.lang)
            : {type: 'ERROR', error}
        );
      });
  });

const toggledFeaturedWinnerEpic = action$ => action$.ofType(WinnersActions.Types.TOGGLE_FEATURED_WINNER_SUCCESS)
  .map(action => ResourceActions.getResourceAction(ResourceApi.Types.PrizeEntry, action.winnerId));

const publishFeaturedWinnersEpic = action$ => action$.ofType(WinnersActions.Types.SUBMIT_PUBLISH_FEATURED_WINNERS)
  .mergeMap(action => (
    PrizeServiceApi.publishFeaturedStories(action.lang)
      .mapTo(WinnersActions.publishFeaturedSuccessAction())
      .catch(error => {
        console.error('Could not publish featured winners.');

        if (error.status === 403) {
          console.error('User account has insufficient permissions.');
        }

        return Observable.of(
          WinnersActions.publishFeaturedErrorAction(),
          WinnersActions.openPublishErrorModalAction()
        );
      })
  ));

export default [
  requestWinnersEpic,
  receivedWinnersEpic,
  saveDateOrderedEpic,
  saveWinnerStoryEpic,
    saveWinnerNotesEpic,
  savedWinnerFieldEpic,
  errorSavingWinnerFieldEpic,
  toggleFeaturedWinnerEpic,
  toggledFeaturedWinnerEpic,
  publishFeaturedWinnersEpic
];



// WEBPACK FOOTER //
// ./public/pages/ListWinners/epics.js